import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { ChartSyncProvider } from "../../components/index.ts";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import {
  usePostureFindings,
  usePostureDrift,
  type PostureFindingPage,
  type PostureWindow,
} from "./use-posture.ts";
import { isFindingsEmpty } from "./vocab.ts";
import { FindingsByCodeAndTier } from "./FindingsByCodeAndTier.tsx";
import { FindingsList } from "./FindingsList.tsx";
import { HostDriftTable } from "./HostDriftTable.tsx";
import { PostureTrend } from "./PostureTrend.tsx";
import "./posture.css";

/**
 * The Posture screen (task 11.10; design 12.2 Posture row). It composes the regions the design calls
 * for over TWO REST reads, both role `viewer`:
 *
 * - `GET /api/v1/posture/findings` → NHI/host findings, as a by-code-and-tier count grid (the
 *   PRIMARY view the top-level state rests on) plus an individual-findings list. Risk tier is
 *   conveyed by icon+text with the highest tiers surfaced prominently (never colour alone).
 * - `GET /api/v1/posture/drift` → host configuration drift alerts (a table) PLUS the only posture
 *   time-series the contract exposes — a `trend` of drift counts, which the trend chart plots. A
 *   findings-over-time trend is surfaced as an honest "unavailable" note rather than fabricated,
 *   because the findings endpoint carries no series (design 12.3).
 *
 * BOTH reads are WINDOWED (they take `from`/`to`), so the window is resolved from the shared
 * time-range and pausing freezes the queries exactly as it freezes the other windowed screens
 * (design 12.2, FR-13 AC2). The findings read drives the scaffold state; the drift region renders
 * its own loading/empty/error state inside the ready screen, so a slow or empty drift read never
 * blanks the whole page.
 *
 * It handles every state a screen must (task 11 preamble; design 12.3):
 * - restricted: both reads are viewer-level; an unknown/below-viewer role sees the restricted state.
 * - loading / error / empty / ready as the findings query resolves; a cold-core waking error defers
 *   to the shell's banner (kept "loading" under it so there is no contradiction).
 */
export function PostureScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();

  const { query: findings, window } = usePostureFindings();
  const { query: drift } = usePostureDrift();

  const state = resolveState(role, findings);

  return (
    <ScreenScaffold
      title="Posture"
      subtitle="NHI findings by code and tier, host drift alerts, and the host-drift trend. Risk tier shown by icon and text with the highest tiers emphasised, every number drilling to its events."
      state={state}
    >
      {findings.data !== undefined && (
        <PostureBody findings={findings.data} drift={drift} window={window} live={live} />
      )}
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from the role and the findings query status. */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  findings: ReturnType<typeof usePostureFindings>["query"],
): ScreenState {
  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the posture view is hidden until it is. The server enforces access regardless."
          : "The posture view needs at least the viewer role.",
    };
  }

  if (findings.data !== undefined) {
    if (isFindingsEmpty(findings.data)) {
      return {
        kind: "empty",
        cause:
          "No NHI or host findings were reported in this window. Widen the time range, or check that the AIS is reporting posture findings. Host drift, if any, still appears below once loaded.",
      };
    }
    return { kind: "ready" };
  }

  if (findings.isError) {
    // The cold-core waking error is the shell's banner; keep "loading" under it (no contradiction).
    if (findings.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(findings.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof usePostureFindings>["query"]["error"]): string {
  if (error === null) return "The posture view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see posture data. The server enforces access.";
    case "network":
      return "The posture request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function PostureBody(props: {
  findings: PostureFindingPage;
  drift: ReturnType<typeof usePostureDrift>["query"];
  window: PostureWindow;
  live: boolean;
}): ReactNode {
  const { findings, drift, window } = props;
  const generatedAt = findings.meta.generated_at;
  const sampling = findings.meta.data_quality.sampling_in_effect;

  return (
    <div className="pst" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="pst__meta">
        {props.live ? "Live" : "Paused"} · window{" "}
        <time dateTime={isoAttr(window.fromMs)}>
          {formatTimeOfDay(window.fromMs)}
        </time>{" "}
        to{" "}
        <time dateTime={isoAttr(window.toMs)}>
          {formatTimeOfDay(window.toMs)}
        </time>{" "}
        · generated{" "}
        <time dateTime={isoAttr(generatedAt)}>
          {formatTimeOfDay(generatedAt)}
        </time>
        {sampling && (
          <span className="pst__meta-flag" role="note">
            {" "}
            · sampling in effect. Some counts may be sampled
          </span>
        )}
      </p>
      </div>

      {/* PRIMARY: NHI findings by code and tier (the aggregate count grid). */}
      <FindingsByCodeAndTier aggregates={findings.by_code_and_tier} />

      {/* The individual findings behind the counts. */}
      <FindingsList findings={findings.items} />

      {/* Host drift is a secondary region: it shows its own state so a slow/empty/failed drift read
          never blanks the page, and it owns both the drift table and the (only) trend series. */}
      <DriftRegion drift={drift} />
    </div>
  );
}

/**
 * The host-drift region: the drift alerts table and the drift trend chart, both sourced from the
 * one `GET /posture/drift` read. It renders its own loading status while the first fetch is in
 * flight and a non-blocking error note on a (non-waking) failure, so one slow or failing drift read
 * never takes down the whole screen. The charts share a crosshair-sync group.
 */
function DriftRegion(props: {
  drift: ReturnType<typeof usePostureDrift>["query"];
}): ReactNode {
  const { drift } = props;

  if (drift.data !== undefined) {
    return (
      <ChartSyncProvider>
        <PostureTrend trend={drift.data.trend} />
        <HostDriftTable entries={drift.data.items} />
      </ChartSyncProvider>
    );
  }
  if (drift.isError && !drift.error.isWaking) {
    return (
      <div className="pst-secondary-state" data-kind="error" role="alert" aria-label="Host drift">
        <strong>Host drift unavailable</strong>
        <span>The host drift and trend could not load. The findings above still apply.</span>
      </div>
    );
  }
  return (
    <div className="pst-secondary-state" role="status" aria-live="polite" aria-label="Host drift">
      <strong>Loading host drift…</strong>
    </div>
  );
}
